import Link from "next/link";
import { useMemo, useState } from "react";
import useSWR, { KeyedMutator } from "swr";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Table,
  Text,
} from "@radix-ui/themes";
import {
  IconChartBarOff,
  IconChevronRight,
  IconExternalLink,
  IconPlus,
  IconReload,
} from "@tabler/icons-react";
import { toast } from "sonner";
import fetchJson from "@/lib/fetchJson";
import { capitalize, formatNumber, freshnessBadge } from "@/lib/util";
import { creatorPriceProfileUrl } from "@/lib/creatorPriceProfileUrl";
import ClearableSelect from "@/components/CustomUiComponents/ClearableSelect";
import Loader from "@/components/CustomUiComponents/Loader";
import {
  PerformanceRanking,
  refreshPerformanceRankings,
  usePerformanceRankings,
} from "@/services/creator";

interface RecommendedCreatorsProps {
  campaign: Campaign;
  mutate: KeyedMutator<Campaign>;
}

const RANK_MEDAL: Record<number, "amber" | "gray" | "bronze"> = {
  1: "amber",
  2: "gray",
  3: "bronze",
};

function RankBadge({ rank }: { rank: number }) {
  const medal = RANK_MEDAL[rank];
  if (medal) {
    return (
      <Badge color={medal} variant="solid" radius="full" highContrast>
        #{rank}
      </Badge>
    );
  }
  return (
    <Text size="2" weight="medium" color="gray">
      #{rank}
    </Text>
  );
}

const RecommendedCreators = ({ campaign, mutate }: RecommendedCreatorsProps) => {
  const [open, setOpen] = useState(false);
  const [addingId, setAddingId] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [format, setFormat] = useState("");
  const [theme, setTheme] = useState("");
  const [vibe, setVibe] = useState("");

  const { data: formats } = useSWR(open ? "/creator/tags" : null, fetchJson);
  const { data: themes } = useSWR(open ? "/creator/theme" : null, fetchJson);
  const { data: vibes } = useSWR(open ? "/creator/vibe" : null, fetchJson);

  // Derive dominant platform from planning budgets (fall back to tiktok)
  const campaignPlatform = useMemo(() => {
    const budgets = campaign.CampaignBudget ?? [];
    const platforms = Array.from(
      new Set(budgets.map((b) => b.platform.toLowerCase())),
    );
    return platforms.length === 1 ? platforms[0] : "tiktok";
  }, [campaign.CampaignBudget]);

  // List all post formats (like the theme/vibe filters). When none is picked,
  // the backend auto-derives the campaign's formats from its planning budgets;
  // picking one here overrides that to narrow the recommendations.
  const formatOptions = useMemo(
    () =>
      (formats || []).map((f: any) => ({
        label: capitalize(f.tag),
        value: String(f.id),
      })),
    [formats],
  );

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("campaignId", String(campaign.id));
    params.set("platform", campaignPlatform);
    params.set("limit", "20");
    params.set("excludeBooked", "true");
    if (format) params.set("format", format);
    if (theme) params.set("theme", theme);
    if (vibe) params.set("vibe", vibe);
    return params;
  }, [campaign.id, campaignPlatform, format, theme, vibe]);

  const { data, isLoading, mutate: mutateRankings } = usePerformanceRankings(
    open ? queryParams : null,
  );

  const peopleLink = useMemo(() => {
    const params = new URLSearchParams();
    params.set("tab", "creators");
    params.set("view", "performance");
    params.set("platform", data?.platform || campaignPlatform);
    // Pass all format IDs from planning budgets so leaderboard pre-filters correctly
    const formatIds = Array.from(
      new Set(
        (campaign.CampaignBudget ?? [])
          .map((b) => b.creatorTypeId)
          .filter(Boolean),
      ),
    );
    if (formatIds.length > 0) {
      params.set("format", formatIds.join(","));
    }
    return `/people?${params.toString()}`;
  }, [campaign.CampaignBudget, data?.platform, campaignPlatform]);

  const handleAddToBudget = async (row: PerformanceRanking) => {
    setAddingId(row.creatorPriceId);
    try {
      await fetchJson("/creator/budget", {
        method: "POST",
        body: JSON.stringify({
          count: 1,
          price: row.creatorPriceId,
          campaign: campaign.id,
        }),
      });
      toast.success(`Added @${row.username} to campaign budget`);
      mutate();
      mutateRankings();
    } catch {
      toast.error("Failed to add creator to budget");
    } finally {
      setAddingId(null);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const result = await refreshPerformanceRankings(queryParams);
      if (result.queued === 0) {
        toast.info(
          result.skipped > 0
            ? "All accounts were scraped within the last 24h — already up to date."
            : "No accounts to refresh.",
        );
      } else {
        toast.success(
          `Queued ${result.queued} account${result.queued === 1 ? "" : "s"} for refresh${
            result.skipped ? ` (${result.skipped} already fresh)` : ""
          }.`,
        );
      }
      mutateRankings();
    } catch {
      toast.error("Failed to queue refresh");
    } finally {
      setRefreshing(false);
    }
  };

  if (!campaign.CampaignBudget?.length) {
    return null;
  }

  const rankings = data?.rankings ?? [];

  return (
    <Flex className="box" direction="column" gap="4" p="5">
      <Flex
        direction="row"
        justify="between"
        align="center"
        wrap="wrap"
        gap="2"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        style={{ cursor: "pointer" }}
      >
        <Flex direction="column" gap="1">
          <Flex align="center" gap="2">
            <IconChevronRight
              size={18}
              className="icon"
              style={{
                transform: open ? "rotate(90deg)" : "none",
                transition: "transform 150ms ease",
              }}
            />
            <Heading size="4">Recommended creators</Heading>
            {open && rankings.length > 0 && (
              <Badge color="gray" variant="soft" radius="full">
                {rankings.length}
              </Badge>
            )}
          </Flex>
          <Text size="2" color="gray" ml="6">
            Top-performing tracked accounts by average views — hit Refresh to pull the latest data before choosing
          </Text>
        </Flex>
        {open && (
          <Flex
            gap="2"
            align="center"
            onClick={(e) => e.stopPropagation()}
          >
            <Button variant="soft" asChild>
              <Link href={peopleLink}>View full leaderboard</Link>
            </Button>
            <Button onClick={handleRefresh} disabled={refreshing} variant="outline">
              <IconReload
                className="icon-accent"
                style={
                  refreshing ? { animation: "spin 1s linear infinite" } : undefined
                }
              />
              {refreshing ? "Refreshing..." : "Refresh"}
            </Button>
          </Flex>
        )}
      </Flex>

      {open && (
        <Flex gap="4" align="center" wrap="wrap">
          <Flex align="center" gap="2">
            <Text size="2" color="gray">
              Format
            </Text>
            <Box width="180px">
              <ClearableSelect
                value={format}
                onValueChange={setFormat}
                placeholder="Campaign default"
                options={formatOptions}
              />
            </Box>
          </Flex>
          <Flex align="center" gap="2">
            <Text size="2" color="gray">
              Theme
            </Text>
            <Box width="180px">
              <ClearableSelect
                value={theme}
                onValueChange={setTheme}
                placeholder="Any"
                options={(themes || []).map((t: any) => ({
                  label: capitalize(t.name),
                  value: String(t.id),
                }))}
              />
            </Box>
          </Flex>
          <Flex align="center" gap="2">
            <Text size="2" color="gray">
              Vibe
            </Text>
            <Box width="180px">
              <ClearableSelect
                value={vibe}
                onValueChange={setVibe}
                placeholder="Any"
                options={(vibes || []).map((v: any) => ({
                  label: capitalize(v.name),
                  value: String(v.id),
                }))}
              />
            </Box>
          </Flex>
        </Flex>
      )}

      {open && (isLoading ? (
        <Loader full={false} />
      ) : rankings.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          gap="3"
          py="8"
          px="5"
        >
          <IconChartBarOff size={36} className="icon" stroke={1.25} />
          <Text size="2" color="gray" align="center" style={{ maxWidth: 420 }}>
            No tracked, unbooked creators with scraped view data match this
            campaign&apos;s formats{format || theme || vibe ? " and the selected filters" : ""}.
            Try clearing filters, ensure accounts have <em>Track</em> enabled, and
            use Refresh to queue a fresh scrape.
          </Text>
        </Flex>
      ) : (
        <Table.Root variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Rank</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Account</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Avg views</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Last scraped</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Price</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell justify="end">Action</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rankings.map((row) => {
              const profileUrl = creatorPriceProfileUrl({
                platform: row.platform,
                username: row.username,
              } as CreatorPrice);

              const freshness = freshnessBadge(row.lastScraped);

              return (
                <Table.Row key={row.creatorPriceId}>
                  <Table.Cell>
                    <RankBadge rank={row.rank} />
                  </Table.Cell>
                  <Table.Cell>
                    <Flex align="center" gap="2">
                      <Avatar
                        src={row.avatar ? `/image-upload/${row.avatar}` : undefined}
                        fallback={row.username[0]?.toUpperCase()}
                        radius="full"
                        size="2"
                      />
                      <Flex direction="column" gap="1">
                        <Link href={`/creators/${row.creatorId}`}>
                          {row.creatorName}
                        </Link>
                        <Flex align="center" gap="1">
                          <Text size="1" color="gray">
                            @{row.username}
                          </Text>
                          {profileUrl && (
                            <a href={profileUrl} target="_blank" rel="noreferrer">
                              <IconExternalLink size={14} className="icon" />
                            </a>
                          )}
                        </Flex>
                      </Flex>
                    </Flex>
                  </Table.Cell>
                  <Table.Cell>
                    <Text weight="bold" size="3" className="text-accent">
                      {row.averageViews != null
                        ? formatNumber(row.averageViews)
                        : "—"}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Flex direction="column" gap="1" align="start">
                      <Text size="2">
                        {row.lastScraped
                          ? new Date(row.lastScraped).toLocaleDateString()
                          : "—"}
                      </Text>
                      <Badge color={freshness.color} size="1">
                        {freshness.label}
                      </Badge>
                    </Flex>
                  </Table.Cell>
                  <Table.Cell>${row.price}</Table.Cell>
                  <Table.Cell justify="end">
                    <Button
                      size="2"
                      onClick={() => handleAddToBudget(row)}
                      disabled={addingId === row.creatorPriceId}
                    >
                      <IconPlus className="icon-accent" />
                      {addingId === row.creatorPriceId ? "Adding..." : "Add to budget"}
                    </Button>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      ))}
    </Flex>
  );
};

export default RecommendedCreators;
